import React, {MouseEvent, ReactNode} from "react";
import {Box, Button, IconButton, Popover, Stack, Tooltip} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {useTheme} from "@mui/material/styles";

interface ToolbarIconButtonProps {
    active?: boolean;
    children: ReactNode;
    disabled?: boolean;
    label: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

interface FormatMenuButtonProps {
    active?: boolean;
    disabled?: boolean;
    icon: ReactNode;
    iconOnly?: boolean;
    label: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    showChevron?: boolean;
}

interface ColorMenuButtonProps {
    activeColor?: string | null;
    defaultIconColor?: string;
    disabled?: boolean;
    icon: ReactNode;
    label: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

interface ColorPaletteButtonProps {
    label: string;
    onClick: () => void;
    selected?: boolean;
    swatch?: string;
    value: string | null;
}

export interface ColorPaletteOption {
    label: string;
    swatch?: string;
    value: string | null;
}

interface ColorPaletteGridProps {
    groups: ColorPaletteOption[][];
    onSelect: (color: string | null) => void;
    selectedColor: string | null;
}

interface ToolbarPopoverProps {
    anchorEl: HTMLButtonElement | null;
    children: ReactNode;
    direction?: 'column' | 'row';
    onClose: () => void;
    open: boolean;
}

export const ToolbarIconButton = ({active, children, disabled, label, onClick}: ToolbarIconButtonProps) => {
    const isDisabled = Boolean(disabled && !active);

    return (
        <Tooltip title={label}>
            <span>
                <IconButton
                    aria-label={label}
                    disabled={isDisabled}
                    onClick={onClick}
                    onMouseDown={(event) => event.preventDefault()}
                    size="small"
                    sx={{
                        bgcolor: active ? 'primary.main' : 'transparent',
                        borderRadius: 1,
                        color: active ? 'primary.contrastText' : 'text.primary',
                        height: 32,
                        width: 32,
                        '&:hover': {
                            bgcolor: active ? 'primary.dark' : 'action.hover',
                        },
                        '&.Mui-disabled': {
                            bgcolor: 'transparent',
                            color: 'action.disabled',
                        },
                    }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
};

export const FormatMenuButton = ({
    active,
    disabled,
    icon,
    iconOnly,
    label,
    onClick,
    showChevron,
}: FormatMenuButtonProps) => {
    const isDisabled = Boolean(disabled && !active);

    const button = (
        <Button
            aria-label={iconOnly ? label : undefined}
            color="primary"
            disabled={isDisabled}
            disableElevation
            onClick={onClick}
            onMouseDown={(event) => event.preventDefault()}
            size="small"
            sx={{
                borderRadius: 1,
                color: active ? 'primary.contrastText' : 'text.primary',
                gap: iconOnly ? 0.25 : 0.75,
                justifyContent: iconOnly ? 'center' : 'space-between',
                minWidth: iconOnly ? 52 : showChevron ? 138 : 132,
                px: iconOnly ? 0.75 : 1,
                textTransform: 'none',
            }}
            variant={active ? 'contained' : 'text'}
        >
            <Stack alignItems="center" direction="row" gap={iconOnly ? 0 : 0.75}>
                {icon}
                {!iconOnly && (
                    <Box component="span" sx={{fontSize: 12, fontWeight: 400, whiteSpace: 'nowrap'}}>
                        {label}
                    </Box>
                )}
            </Stack>
            {showChevron && <KeyboardArrowDownIcon fontSize="small"/>}
        </Button>
    );

    if (!iconOnly) {
        return button;
    }

    return (
        <Tooltip title={label}>
            <span>{button}</span>
        </Tooltip>
    );
};

export const ColorMenuButton = ({
    activeColor,
    defaultIconColor,
    disabled,
    icon,
    label,
    onClick,
}: ColorMenuButtonProps) => {
    const iconColor = activeColor || defaultIconColor || 'text.primary';

    return (
        <Tooltip title={label}>
            <span>
                <IconButton
                    aria-label={label}
                    disabled={disabled}
                    onClick={onClick}
                    onMouseDown={(event) => event.preventDefault()}
                    size="small"
                    sx={{
                        bgcolor: 'transparent',
                        borderRadius: 1,
                        color: iconColor,
                        height: 32,
                        width: 32,
                        '&:hover': {
                            bgcolor: 'action.hover',
                        },
                        '&.Mui-disabled': {
                            bgcolor: 'transparent',
                            color: 'action.disabled',
                        },
                    }}
                >
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );
};

const ColorPaletteButton = ({label, onClick, selected, swatch, value}: ColorPaletteButtonProps) => {
    const theme = useTheme();
    const checkColor = swatch ? theme.palette.getContrastText(swatch) : 'text.primary';

    return (
        <IconButton
            aria-label={label}
            onClick={onClick}
            onMouseDown={(event) => event.preventDefault()}
            size="small"
            sx={{
                bgcolor: swatch || 'background.paper',
                border: value ? 0 : 1,
                borderColor: 'divider',
                borderRadius: '50%',
                color: 'text.secondary',
                height: 30,
                p: 0,
                width: 30,
                '&:hover': {
                    bgcolor: swatch || 'action.hover',
                    opacity: swatch ? 0.86 : 1,
                },
            }}
        >
            {selected && <CheckIcon sx={{color: checkColor, fontSize: 21}}/>}
            {!selected && !value && <FormatColorResetIcon sx={{fontSize: 17}}/>}
        </IconButton>
    );
};

export const ColorPaletteGrid = ({groups, onSelect, selectedColor}: ColorPaletteGridProps) => (
    <Stack gap={0.75}>
        {groups.map((group, index) => (
            <Stack direction="row" gap={0.75} key={index}>
                {group.map((option) => (
                    <ColorPaletteButton
                        key={option.label}
                        label={option.label}
                        onClick={() => onSelect(option.value)}
                        selected={selectedColor === option.value}
                        swatch={option.swatch}
                        value={option.value}
                    />
                ))}
            </Stack>
        ))}
    </Stack>
);

export const ToolbarPopover = ({anchorEl, children, direction = 'column', onClose, open}: ToolbarPopoverProps) => (
    <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
        }}
        onClose={onClose}
        open={open}
        PaperProps={{
            sx: {
                bgcolor: '#fff',
                mt: 0.5,
                p: 0.5,
            },
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
        }}
    >
        <Stack direction={direction} gap={0.5}>
            {children}
        </Stack>
    </Popover>
);
